import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';
import { createProductionOrder } from '@/api/productionOrder.api';
import { getItemsWithVariantsForBOM, type BOMDropdownGroup } from '@/api/item.api';
import bomAPI, { type BOM } from '@/api/bom.api';
import { useWarehouseStore } from '@/stores/warehouseStore';

interface SuggestedItem {
  id: string;
  name: string;
  code: string;
  isVariant?: boolean;
  variantName?: string | null;
  unitName?: string;
  currentStock: number;
  reorderPoint: number;
  suggestQty: number;
  bomId: string | null;
  bomVersion?: string;
  checked: boolean;
}

interface MTSSuggestModalProps {
  onClose: () => void;
  onCreated: () => void;
}

export default function MTSSuggestModal({
  onClose,
  onCreated,
}: MTSSuggestModalProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const { selectedWarehouseId } = useWarehouseStore();
  const [items, setItems] = useState<SuggestedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      setLoading(true);
      try {
        const [itemsRes, bomsRes] = await Promise.all([
          getItemsWithVariantsForBOM(selectedWarehouseId || undefined),
          bomAPI.getAll({ warehouseId: selectedWarehouseId || undefined, status: 'ACTIVE', limit: 1000 }),
        ]);

        const rawItems: BOMDropdownGroup[] = Array.isArray(itemsRes) ? itemsRes : (itemsRes as any).data || [];
        const boms: BOM[] = bomsRes.data || [];

        const suggestions: SuggestedItem[] = [];

        rawItems.forEach((group) => {
          if (group.variants && group.variants.length > 0) {
            group.variants.forEach((v) => {
              const matchedBOM = boms.find((b) => b.product_id === v.id && b.status !== 'OBSOLETE');
              suggestions.push({
                id: v.id,
                name: group.name,
                code: v.code || group.code,
                isVariant: true,
                variantName: v.name,
                unitName: group.unit_name || null,
                currentStock: 0,
                reorderPoint: 10,
                suggestQty: 20,
                bomId: matchedBOM ? matchedBOM.id : null,
                bomVersion: matchedBOM ? `v${matchedBOM.version}` : undefined,
                checked: !!matchedBOM,
              });
            });
          } else {
            const matchedBOM = boms.find((b) => b.product_id === group.id && b.status !== 'OBSOLETE');
            suggestions.push({
              id: group.id,
              name: group.name,
              code: group.code,
              isVariant: false,
              variantName: null,
              unitName: group.unit_name || 'Pcs',
              currentStock: 0,
              reorderPoint: 10,
              suggestQty: 20,
              bomId: matchedBOM ? matchedBOM.id : null,
              bomVersion: matchedBOM ? `v${matchedBOM.version}` : undefined,
              checked: !!matchedBOM,
            });
          }
        });

        if (isMounted) {
          setItems(suggestions.filter((s) => s.bomId !== null));
        }
      } catch (err: any) {
        if (isMounted) {
          setItems([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadData();
    return () => {
      isMounted = false;
    };
  }, []);

  const toggleCheck = useCallback((itemId: string) => {
    setItems((prev) =>
      prev.map((si) =>
        si.id === itemId ? { ...si, checked: !si.checked } : si,
      ),
    );
  }, []);

  const toggleAll = useCallback(() => {
    const allChecked = items.every((si) => si.checked);
    setItems((prev) => prev.map((si) => ({ ...si, checked: !allChecked })));
  }, [items]);

  const updateQty = useCallback((itemId: string, qty: number) => {
    setItems((prev) =>
      prev.map((si) =>
        si.id === itemId ? { ...si, suggestQty: Math.max(1, qty) } : si,
      ),
    );
  }, []);

  const checkedItems = items.filter((si) => si.checked && si.bomId);

  const handleCreateSinglePRD = useCallback((si: SuggestedItem) => {
    onClose();
    navigate('/manufacturing/production-orders/new', {
      state: {
        type: 'MTS',
        productId: si.id,
        productName: si.name,
        productCode: si.code,
        bomId: si.bomId || undefined,
        plannedQty: si.suggestQty,
        unit: si.unitName || 'Pcs',
        priority: 'NORMAL',
        notes: `Auto-suggested MTS replenishment`,
      },
    });
  }, [navigate, onClose]);

  const handleCreate = useCallback(async () => {
    if (checkedItems.length === 0) {
      toast.error('Please select at least one item with an active BOM');
      return;
    }

    setCreating(true);
    let successCount = 0;

    for (const si of checkedItems) {
      try {
        const today = new Date().toISOString().split('T')[0];
        const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
        await createProductionOrder({
          type: 'MTS',
          priority: 'NORMAL',
          itemId: si.id,
          bomId: si.bomId!,
          plannedQty: si.suggestQty,
          plannedStartDate: today,
          plannedEndDate: nextWeek,
          warehouseId: selectedWarehouseId || '',
          notes: `MTS Replenishment suggestion`,
        });
        successCount++;
      } catch (err) {
        console.error('Failed to create MTS order for item:', si.id, err);
      }
    }

    setCreating(false);
    if (successCount > 0) {
      toast.success(`${successCount} MTS Production Orders created`);
      onCreated();
      onClose();
    } else {
      toast.error('Failed to create production orders');
    }
  }, [checkedItems, toast, onCreated, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-[840px] max-w-[95vw] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              MTS Production Suggestions
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Items with active BOMs available for replenishment
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center py-10 text-sm text-slate-500">
              <i className="ri-loader-4-line animate-spin text-2xl mb-2 block text-indigo-600" />
              Checking inventory and active BOMs...
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-emerald-50 border border-emerald-200 mb-3">
                <i className="ri-check-double-line text-xl text-emerald-500" />
              </div>
              <p className="text-sm font-medium text-gray-700">
                No items eligible for replenishment
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Ensure products have active BOMs created
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={items.every((si) => si.checked)}
                    onChange={toggleAll}
                    className="w-4 h-4 rounded border-gray-300 text-[#4f46e5] focus:ring-indigo-500"
                  />
                  <span className="text-xs font-medium">
                    Select all ({items.filter((si) => si.checked).length}/
                    {items.length})
                  </span>
                </label>
              </div>

              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 w-10"></th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">
                      Item
                    </th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">
                      Variant
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">
                      BOM Version
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">
                      Suggested Qty
                    </th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">
                      Unit
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((si) => (
                    <tr
                      key={si.id}
                      className="border-b border-slate-50 hover:bg-slate-50/50"
                    >
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={si.checked}
                          onChange={() => toggleCheck(si.id)}
                          className="w-4 h-4 rounded border-gray-300 text-[#4f46e5] focus:ring-indigo-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-sm font-medium text-gray-800">
                          {si.name}
                        </div>
                        <div className="text-[11px] text-gray-400">
                          {si.code}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        {si.isVariant && si.variantName ? (
                          <span className="text-xs text-gray-600">
                            {si.variantName}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right text-xs text-indigo-600 font-medium">
                        {si.bomVersion || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <input
                          type="number"
                          min={1}
                          value={si.suggestQty}
                          onChange={(e) =>
                            updateQty(
                              si.id,
                              parseInt(e.target.value, 10) || 1,
                            )
                          }
                          className="w-20 text-right text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-3 py-2.5 text-left text-sm text-gray-600">
                        {si.unitName || 'Pcs'}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        {si.bomId ? (
                          <button
                            type="button"
                            onClick={() => handleCreateSinglePRD(si)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white rounded-lg transition-colors cursor-pointer border border-indigo-200"
                            title="Pre-fill and create Production Order"
                          >
                            <i className="ri-add-line text-xs" />
                            <span>Create PRD</span>
                          </button>
                        ) : (
                          <span className="text-[11px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                            No BOM
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          {items.length > 0 && (
            <button
              onClick={handleCreate}
              disabled={creating || checkedItems.length === 0}
              className="px-4 py-2 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {creating ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <i className="ri-add-line" />
                  Create Selected Orders ({checkedItems.length})
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
